import { useEffect, useState } from "react";
import type { Regimen } from "@medassist/shared";
import { isMedItemActive } from "@medassist/shared";
import { getRegimens, ApiError } from "../../../lib/api.js";
import { PillIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

export function MedicationsWidget({ patient, events, onNavigate, onError }: WidgetProps) {
  const [regimens, setRegimens] = useState<Regimen[] | null>(null);

  useEffect(() => {
    getRegimens(patient.patientId)
      .then(setRegimens)
      .catch((err) => onError(err instanceof ApiError ? err.message : "Couldn't load medications"));
  }, [patient.patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeRegimens = regimens?.filter(
    (r) => r.kind === "scheduled" && r.items.some((i) => isMedItemActive(i))
  ) ?? [];
  const prnMeds = regimens?.filter((r) => r.kind === "as_needed_inventory" && r.items.some((i) => isMedItemActive(i))) ?? [];

  const dosesToday = events.filter(
    (e) => (e.eventType === "medication_taken" || e.eventType === "as_needed_med") && isToday(e.occurredAt)
  ).length;

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <PillIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Medications</h2>
        <button onClick={() => onNavigate("regimens")} className="ml-auto text-xs text-(--accent) hover:underline">
          View all
        </button>
      </div>
      {regimens === null ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <div className="flex justify-between">
            <span>Scheduled regimens</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{activeRegimens.length}</span>
          </div>
          <div className="flex justify-between">
            <span>PRN medications</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{prnMeds.length}</span>
          </div>
          <div className="flex justify-between border-t border-gray-100 dark:border-gray-800 pt-2">
            <span>Doses logged today</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{dosesToday}</span>
          </div>
        </div>
      )}
    </section>
  );
}
